import React, { useRef, useState } from 'react'

import Image from 'next/image'
import { Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/modal'
import { Avatar, cn, Spinner } from '@nextui-org/react'
import { TelegramIcon, TelegramShareButton, TwitterShareButton, XIcon } from 'react-share'
import { useSearchParams } from 'next/navigation'
import { toast } from 'react-toastify'

import { useAppSelector } from '@/hooks'
import ICONS from '@/assets/icons'
import EmojiPost from '@/app/(global)/feed/components/Emoji'
import { base_url, handleCopy, handleShowMessage } from '@/utils'
import { useSharePostMutation } from '@/redux/api/feed/feed.w3api'
import { TypeSharePost } from '@/redux/api/feed/feed.type'

import styles from './styles.module.css'

type EditProfileProps = {
  isOpen: boolean
  toggleOpen: () => void
  postId: string
}

export const ModalSharePost = ({ isOpen, toggleOpen, postId }: EditProfileProps) => {
  const [textPost, setTextPost] = useState<string>('')
  const user = useAppSelector((state) => state.root.auth.user)
  const [isOpenEmoji, setIsOpenEmoji] = useState<boolean>(false)
  const getParams = useSearchParams().get('tab') as string
  const [sharePost, { isLoading }] = useSharePostMutation()
  const spaceActive = useAppSelector((state) => state.root.settings.spaceActive)

  const onSubmit = async () => {
    if (textPost === '' && !user) {
      return
    }

    try {
      const postShare = {
        content: textPost,
        postId: postId,
        isPublic: true,
      } as TypeSharePost

      if (spaceActive) {
        postShare.spaceId = spaceActive
      }
      await sharePost(postShare).unwrap()
      setTextPost('')
      toast.success('Post shared.')
    } catch (error: any) {
      toast.error(handleShowMessage(error?.data?.message) || 'Share post failed, please try again!')
    } finally {
      toggleOpen()
    }
  }

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 350)}px`
    }
  }
  const handleTextChange = async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textInput = e.target.value
    setTextPost(textInput)
    autoResize()
  }

  return (
    <Modal
      classNames={{
        base: 'overflow-visible text-white',
      }}
      className="bg-ss-dark"
      hideCloseButton={true}
      size="2xl"
      isOpen={isOpen}
      onOpenChange={toggleOpen}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-6">
          <Image
            onClick={toggleOpen}
            className="cursor-pointer"
            src={ICONS.icCloseWhite}
            width={22}
            height={22}
            alt=""
          />
          <h3 className="text-base font-medium text-white">Share</h3>
        </ModalHeader>

        <div className=" flex gap-3 border-b border-t border-white/10 p-5">
          <Avatar
            showFallback
            src={user?.avatarImage || ''}
            fallback={<Image src="/Sightsea_icon.png" alt="Avatar" width={40} height={40} />}
            className={'h-[40px] w-[40px]'}
          />
          <div className="flex flex-1 flex-col gap-2">
            <textarea
              ref={textareaRef}
              className={cn(styles.textareaP)}
              name="textPost"
              value={textPost}
              onChange={handleTextChange}
              placeholder="Say something about this..."
            />
            <div className="flex justify-between items-center">
              <div className="relative cursor-pointer">
                <Image
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsOpenEmoji((prev) => !prev)
                  }}
                  src={ICONS.emoji}
                  width={20}
                  height={20}
                  alt="Emoji"
                />

                <EmojiPost
                  onSelectIcon={(emoji) => {
                    setTextPost((prev: string) => prev + emoji)
                  }}
                  isOpen={isOpenEmoji}
                  position={'top'}
                  onToggleOpen={() => setIsOpenEmoji(!isOpenEmoji)}
                />
              </div>
              <button
                onClick={onSubmit}
                disabled={isLoading}
                className={cn('flex items-center gap-1 rounded-2xl bg-[#151531] px-4 py-2 text-xs font-medium')}
              >
                {isLoading && <Spinner size="sm" />} Share now
              </button>
            </div>
          </div>
        </div>
        <ModalBody className="py-4">
          <p className="text-sm font-medium">Share to</p>
          <div className="flex gap-6">
            <div
              onClick={() => {
                handleCopy(`/feed?tab=${getParams}&shareID=${postId}`, 'link')
              }}
              className="flex flex-col gap-1 items-center"
            >
              <div className="cursor-pointer bg-ss-grayscale-900 rounded-full p-4">
                <Image src={ICONS.linkIcon} width={22} height={22} alt="" />
              </div>
              <p className="text-xs text-ss-grayscale-400 font-medium">Copy link</p>
            </div>
            {/* <div className="flex flex-col gap-1 items-center">
              <div className="cursor-pointer bg-ss-grayscale-900 rounded-full p-4">
                <Image src={ICONS.message} width={22} height={22} alt="" />
              </div>
              <p className="text-xs text-ss-grayscale-400 font-medium">Message</p>
            </div> */}
            <div className="flex flex-col gap-1 items-center">
              <TwitterShareButton
                url={new URL(`/feed?tab=${getParams}&shareID=${postId}`, base_url).href}
                className="!p-2.5 !bg-ss-grayscale-900 rounded-full"
              >
                <XIcon
                  round
                  size={32}
                  bgStyle={{
                    fill: '#161B26',
                  }}
                />
              </TwitterShareButton>

              <p className="text-xs text-ss-grayscale-400 font-medium">X</p>
            </div>
            <div className="flex flex-col gap-1 items-center">
              <TelegramShareButton
                url={new URL(`/feed?tab=${getParams}&shareID=${postId}`, base_url).href}
                className="!p-2.5 !bg-ss-grayscale-900 rounded-full"
              >
                <TelegramIcon
                  round
                  size={32}
                  bgStyle={{
                    fill: '#161B26',
                  }}
                />
              </TelegramShareButton>

              <p className="text-xs text-ss-grayscale-400 font-medium">Telegram</p>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
